/**
 * Assessment Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox,
  FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AssessmentUpdate, AssignedClassDisplay } from '../../types/assessment';
import { Subject } from '../../types/subject';
import { GRADE_LEVELS } from '../../types/common';

const AssessmentEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<AssessmentUpdate>({
    title: '',
    description: '',
    max_points: 0,
    due_date: '',
    is_active: true,
    grade_level: null,
    subject: null,
    class_id: 0,
    rubric_id: null,
    assigned_class_ids: [],
    submission_format: 'file',
  });

  const [teacherName, setTeacherName] = useState<string>('');

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Dropdown data
  const [classes, setClasses] = useState<any[]>([]);
  const [rubrics, setRubrics] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [classesRes, rubricsRes, subjectsRes] = await Promise.all([
          axios.get('/api/school-classes/'),
          axios.get('/api/rubrics/'),
          axios.get('/api/subjects/?ordering=name&page_size=1000'),
        ]);

        setClasses(classesRes.data.results || []);
        setRubrics(rubricsRes.data.results || []);
        setSubjects(subjectsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading dropdown data:', err);
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!id) {
        setError('No assessment ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/assignments/${id}/`);
        const assessment = response.data;

        // Convert ISO datetime to datetime-local format (remove timezone)
        let formattedDate = '';
        if (assessment.due_date) {
          // Remove 'Z' timezone and milliseconds, keep only yyyy-MM-ddThh:mm:ss
          formattedDate = assessment.due_date.replace('Z', '').split('.')[0];
          // datetime-local accepts yyyy-MM-ddThh:mm or yyyy-MM-ddThh:mm:ss
        }

        // Initialize assigned_class_ids from assigned_classes_display or fall back to class_assigned
        const assignedClassIds = assessment.assigned_classes_display && assessment.assigned_classes_display.length > 0
          ? assessment.assigned_classes_display.map((cls: AssignedClassDisplay) => cls.id)
          : (assessment.class_assigned ? [assessment.class_assigned] : []);

        setFormData({
          title: assessment.title || '',
          description: assessment.description || '',
          max_points: assessment.max_points || 0,
          due_date: formattedDate,
          is_active: assessment.is_active ?? true,
          grade_level: assessment.grade_level || null,
          subject: assessment.subject || null,
          class_id: assessment.class_assigned || 0,
          rubric_id: assessment.rubric || null,
          assigned_class_ids: assignedClassIds,
          submission_format: assessment.submission_format || 'file',
        });
        setTeacherName(assessment.teacher_name || 'Unknown');
      } catch (err: any) {
        console.error('Error fetching assessment:', err);
        if (err.response?.status === 404) {
          setError('Assessment not found');
        } else {
          setError('Failed to load assessment data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchAssessment();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Ensure at least one class is selected
    if (!formData.assigned_class_ids || formData.assigned_class_ids.length === 0) {
      errors.assigned_class_ids = 'Please select at least one class';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? 0 : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleClassToggle = (classId: number) => {
    setFormData(prev => {
      const currentIds = prev.assigned_class_ids || [];
      const newIds = currentIds.includes(classId)
        ? currentIds.filter(id => id !== classId)
        : [...currentIds, classId];
      return {
        ...prev,
        assigned_class_ids: newIds,
        // Also update class_id to the first selected class for backward compatibility
        class_id: newIds.length > 0 ? newIds[0] : 0
      };
    });

    // Clear validation error when user selects a class
    if (validationErrors.assigned_class_ids) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.assigned_class_ids;
        return newErrors;
      });
    }
  };

  // Filter classes by grade level and subject if they are set
  const filteredClasses = classes.filter(cls => {
    if (formData.grade_level && cls.grade_level !== formData.grade_level) return false;
    if (formData.subject && cls.subject !== formData.subject) return false;
    return true;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: AssessmentUpdate = {};

      if (formData.title && formData.title.trim() !== '') payload.title = formData.title;
      if (formData.description && formData.description.trim() !== '') payload.description = formData.description;
      if (formData.max_points !== undefined) payload.max_points = formData.max_points;
      if (formData.due_date && formData.due_date.trim() !== '') payload.due_date = formData.due_date;
      payload.is_active = formData.is_active;
      if (formData.grade_level !== undefined) payload.grade_level = formData.grade_level;
      if (formData.subject !== undefined) payload.subject = formData.subject;
      if (formData.assigned_class_ids && formData.assigned_class_ids.length > 0) {
        payload.assigned_class_ids = formData.assigned_class_ids;
      }
      if (formData.submission_format) payload.submission_format = formData.submission_format;

      await axios.patch(`/api/assignments/${id}/`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/assessments');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating assessment:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update assessment. Please check your input.');
        }
      } else {
        setError('Failed to update assessment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.title) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/assessments')} sx={{ mt: 2 }}>
          Back to Assessments
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/assessments')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Assessments
        </MuiLink>
        <Typography color="text.primary">Edit Assessment</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Assessment</Typography>
        <Typography variant="body2" color="textSecondary">Update the assessment details</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Assessment updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth label="Title" name="title"
              value={formData.title || ''} onChange={handleChange}
              error={!!validationErrors.title} helperText={validationErrors.title}
              disabled={loading}
            />

            <TextField
              fullWidth label="Description" name="description"
              value={formData.description || ''} onChange={handleChange}
              disabled={loading} multiline rows={3}
            />

            <TextField
              fullWidth
              label="Teacher"
              value={teacherName}
              disabled
              InputProps={{
                readOnly: true,
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  value={formData.grade_level || ''}
                  onChange={(e) => handleSelectChange('grade_level', e.target.value)}
                  label="Grade Level"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {GRADE_LEVELS.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject || ''}
                  onChange={(e) => handleSelectChange('subject', e.target.value)}
                  label="Subject"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {subjects.map((subj) => (
                    <MenuItem key={subj.id} value={subj.name}>
                      {subj.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth type="number" label="Max Points" name="max_points"
                value={formData.max_points || ''} onChange={handleChange}
                error={!!validationErrors.max_points} helperText={validationErrors.max_points}
                disabled={loading} inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                fullWidth type="datetime-local" label="Due Date" name="due_date"
                value={formData.due_date || ''} onChange={handleChange}
                disabled={loading} InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Rubric</InputLabel>
              <Select
                value={formData.rubric_id || ''}
                onChange={(e) => handleSelectChange('rubric_id', e.target.value)}
                label="Rubric"
                disabled={loading || loadingDropdowns}
              >
                <MenuItem value="">
                  <em>None (Optional)</em>
                </MenuItem>
                {rubrics.map((rubric) => (
                  <MenuItem key={rubric.id} value={rubric.id}>
                    {rubric.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Submission Format</InputLabel>
              <Select
                value={formData.submission_format || 'file'}
                onChange={(e) => handleSelectChange('submission_format', e.target.value)}
                label="Submission Format"
                disabled={loading}
              >
                <MenuItem value="file">File Upload</MenuItem>
                <MenuItem value="text">Text/Code Paste</MenuItem>
                <MenuItem value="file_text">File and Text</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Linked Classes {formData.assigned_class_ids && formData.assigned_class_ids.length > 0 &&
                  `(${formData.assigned_class_ids.length} selected)`}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select one or more classes to link this assignment to. All linked classes will share the same assignment, title, due date, and rubric.
              </Typography>

              {validationErrors.assigned_class_ids && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationErrors.assigned_class_ids}
                </Alert>
              )}

              {loadingDropdowns ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredClasses.length === 0 ? (
                <Alert severity="info">
                  No classes match the selected grade level and subject. Adjust the filters above to see available classes.
                </Alert>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {filteredClasses.map((cls, index) => {
                      const isChecked = (formData.assigned_class_ids || []).includes(cls.id);
                      return (
                        <React.Fragment key={cls.id}>
                          {index > 0 && <Divider />}
                          <ListItem disablePadding>
                            <ListItemButton onClick={() => handleClassToggle(cls.id)} disabled={loading}>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isChecked}
                                  tabIndex={-1}
                                  disableRipple
                                  disabled={loading}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={cls.name}
                                secondary={`Grade ${cls.grade_level} - ${cls.subject}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active ?? true}
                  onChange={handleChange}
                  name="is_active"
                  disabled={loading}
                />
              }
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/assessments')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Assessment'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AssessmentEditForm;
